/**
 * Pipeline Corrosion Detection Utilities
 *
 * This module provides functions for calculating perpendicular distances from point cloud
 * points to a cylinder axis, applying color gradients for visualization, and generating
 * statistical analysis of corrosion severity.
 *
 * Key Features:
 * - Perpendicular distance calculation from points to cylinder axis
 * - Statistical analysis with health scores and severity classifications
 * - Integration with Potree's elevation attribute system for shader-based visualization
 * - CSV export functionality for reporting
 */

import { log as debugLog, warn as debugWarn } from '@/utils/debugLogger';

const THREE = window.THREE;

/**
 * Calculate minimum value in array without stack overflow
 * (Avoids spread operator which causes stack overflow on large arrays)
 */
function calculateMin(array) {
  let min = Infinity;
  for (let i = 0; i < array.length; i++) {
    if (array[i] < min) min = array[i];
  }
  return min;
}

/**
 * Calculate maximum value in array without stack overflow
 * (Avoids spread operator which causes stack overflow on large arrays)
 */
function calculateMax(array) {
  let max = -Infinity;
  for (let i = 0; i < array.length; i++) {
    if (array[i] > max) max = array[i];
  }
  return max;
}

/**
 * Calculate average value in array without stack overflow
 * (Avoids reduce on very large arrays which can be slow)
 */
function calculateAverage(array) {
  let sum = 0;
  for (let i = 0; i < array.length; i++) {
    sum += array[i];
  }
  return sum / array.length;
}

/**
 * Filter nodes by clip volume - SIMPLIFIED VERSION
 *
 * NOTE: Node-level filtering is DISABLED because:
 * - Visible nodes (typically ~27) are high-level octree nodes with LARGE bounding boxes
 * - Clip volumes are often SMALLER than these node bounding boxes
 * - A node's bbox may not intersect the clip volume, but individual POINTS inside
 *   that node may still be inside the clip volume
 * - This is a spatial data structure granularity mismatch
 *
 * SOLUTION: Process ALL visible nodes, filter individual points (more accurate)
 * Point-level filtering happens at lines 187-192 in calculatePointDistancesToCylinder
 *
 * @param {Array} nodes - Array of point cloud nodes
 * @param {Array} clipVolumes - Array of active clip volumes
 * @returns {Array} All nodes (filtering happens at point level)
 */
function filterNodesByClipVolume(nodes, clipVolumes) {
  if (!clipVolumes || clipVolumes.length === 0) {
    debugLog('[Corrosion Clip] No active clip volumes - processing all nodes');
    return nodes;
  }

  // When clip volumes are active, still process ALL nodes
  // Individual point filtering is more accurate and happens later
  debugLog(`[Corrosion Clip] Clip volumes active - processing all ${nodes.length} nodes (filtering at point level for accuracy)`);
  return nodes;
}

/**
 * Test if a point is inside a clip volume
 * @param {THREE.Vector3} worldPoint - Point in world coordinates
 * @param {Object} clipVolume - Clip volume object with matrixWorld and scale
 * @returns {boolean} True if point is inside the clip volume
 */
function isPointInClipVolume(worldPoint, clipVolume) {
  // Transform point to clip volume's local space
  const localPoint = worldPoint.clone();
  const inverseMatrix = clipVolume.matrixWorld.clone().invert();
  localPoint.applyMatrix4(inverseMatrix);

  // Clip volumes are unit cubes [-0.5, 0.5] in local space
  // The matrixWorld already includes scale, so after inverse transform,
  // we just need to check against the unit cube bounds
  const halfDim = 0.5;

  return Math.abs(localPoint.x) <= halfDim &&
         Math.abs(localPoint.y) <= halfDim &&
         Math.abs(localPoint.z) <= halfDim;
}

/**
 * Check if a point is inside any of the active clip volumes
 * @param {THREE.Vector3} worldPoint - Point in world coordinates
 * @param {Array} clipVolumes - Array of active clip volumes
 * @returns {boolean} True if point is inside at least one clip volume
 */
function isPointInAnyClipVolume(worldPoint, clipVolumes) {
  if (!clipVolumes || clipVolumes.length === 0) {
    return true; // No clip volumes active, include all points
  }

  return clipVolumes.some(volume => isPointInClipVolume(worldPoint, volume));
}

/**
 * Calculate perpendicular distance from each point in the point cloud to the cylinder axis
 *
 * Algorithm:
 * 1. For each point P, calculate vector from cylinder center to point: V = P - C
 * 2. Project V onto cylinder axis: projection = dot(V, A)
 * 3. Find closest point on axis: axisPoint = C + A × projection
 * 4. Calculate perpendicular vector: perpendicular = P - axisPoint
 * 5. Calculate distance: distance = length(perpendicular)
 * 6. Calculate deviation from expected radius: deviation = |distance - expectedRadius|
 *
 * @param {Object|Object[]} pointCloudInput - Potree point cloud object or array of point clouds
 * @param {Object} cylinderData - Cylinder parameters {position, axis, radius, height}
 * @param {Function} progressCallback - Optional callback for progress updates (progress: 0-100)
 * @param {Array} clipVolumes - Optional array of active clip volumes for filtering
 * @returns {Promise<Float32Array>} Array of distance deviations for each point
 */
export async function calculatePointDistancesToCylinder(pointCloudInput, cylinderData, progressCallback = null, clipVolumes = null) {
  const pointClouds = Array.isArray(pointCloudInput)
    ? pointCloudInput.filter(Boolean)
    : [pointCloudInput];

  if (!pointClouds.length || !cylinderData) {
    throw new Error('At least one point cloud and cylinder data are required');
  }

  const clipVolumeActive = clipVolumes && clipVolumes.length > 0;

  // NOTE: Lasso filtering is handled separately in recalculateCorrosionForLassoSelection()
  // This function should return ALL visible points (only filtered by clip volumes)
  // to ensure consistent statistics calculation

  debugLog('[Corrosion] Starting distance calculation...', {
    cylinderPosition: cylinderData.position,
    cylinderAxis: cylinderData.axis,
    cylinderRadius: cylinderData.radius,
    cylinderHeight: cylinderData.height,
    clipVolumesActive: clipVolumeActive,
    clipVolumesCount: clipVolumeActive ? clipVolumes.length : 0
  });

  const { position: cylinderCenter, axis: cylinderAxis, radius: cylinderRadius } = cylinderData;

  // Normalize cylinder axis
  const axisNormalized = cylinderAxis.clone().normalize();

  // Collect all points from all loaded nodes
  const allPoints = [];
  let totalPoints = 0;
  let filteredOutByClipVolume = 0;
  let totalNodesProcessed = 0;

  const totalVisibleNodes = pointClouds.reduce(
    (sum, cloud) => sum + (cloud?.visibleNodes?.length || 0),
    0
  );

  debugLog('[Corrosion 🔍] Inspecting point cloud nodes...');
  debugLog('[Corrosion 🔍] Total visible nodes across clouds:', totalVisibleNodes);

  // Iterate through filtered nodes in each point cloud octree
  pointClouds.forEach((pointCloud, cloudIndex) => {
    if (!pointCloud || !pointCloud.visibleNodes || pointCloud.visibleNodes.length === 0) {
      debugWarn(`[Corrosion ⚠️] Point cloud ${cloudIndex}: No visible nodes available for processing`);
      return;
    }

    const nodesToProcess = clipVolumeActive
      ? filterNodesByClipVolume(pointCloud.visibleNodes, clipVolumes)
      : pointCloud.visibleNodes;

    totalNodesProcessed += nodesToProcess.length;

    nodesToProcess.forEach((node, nodeIndex) => {

      // Try different possible locations for geometry
      let geometry = null;
      let matrixWorld = node.matrixWorld;

      if (node.geometry && node.geometry.attributes && node.geometry.attributes.position) {
        geometry = node.geometry;
      } else if (node.sceneNode && node.sceneNode.geometry && node.sceneNode.geometry.attributes && node.sceneNode.geometry.attributes.position) {
        geometry = node.sceneNode.geometry;
        matrixWorld = node.sceneNode.matrixWorld || matrixWorld;
      } else if (node.geometryNode && node.geometryNode.geometry && node.geometryNode.geometry.attributes && node.geometryNode.geometry.attributes.position) {
        geometry = node.geometryNode.geometry;
      } else {
        debugWarn(`[Corrosion ⚠️] Cloud ${cloudIndex} Node ${nodeIndex}: No geometry found with position attribute`);
      }

      if (geometry) {
        const positions = geometry.attributes.position.array;
        const numPoints = positions.length / 3;

        for (let i = 0; i < numPoints; i++) {
          const localPoint = new THREE.Vector3(
            positions[i * 3],
            positions[i * 3 + 1],
            positions[i * 3 + 2]
          );

          // Transform to world space
          const worldPoint = localPoint.clone().applyMatrix4(matrixWorld);

          // Check if point is inside clip volume (if clip volumes are active)
          if (clipVolumeActive) {
            if (!isPointInAnyClipVolume(worldPoint, clipVolumes)) {
              filteredOutByClipVolume++;
              continue; // Skip points outside clip volumes
            }
          }

          // NOTE: Lasso filtering is NOT done here - it's handled in recalculateCorrosionForLassoSelection()
          // This ensures we always have the full dataset for consistent statistics

          allPoints.push({
            worldPoint,
            nodeIndex: totalPoints,
            node: node,
            cloudIndex
          });

          totalPoints++;
        }
      }
    });
  });

  if (clipVolumeActive) {
    const totalEvaluatedPoints = totalPoints + filteredOutByClipVolume;
    debugLog(`[Corrosion Filter] Clip volume filtering summary:`, {
      totalVisibleNodes,
      nodesAfterFilter: totalNodesProcessed,
      pointsIncluded: totalPoints,
      pointsFilteredByClipVolume: filteredOutByClipVolume,
      filterPercentage: totalEvaluatedPoints > 0
        ? `${((totalPoints / totalEvaluatedPoints) * 100).toFixed(1)}% included`
        : 'N/A'
    });
  }

  debugLog(`[Corrosion] Processing ${totalPoints} points across ${totalNodesProcessed} nodes from ${pointClouds.length} cloud(s)`);

  // Calculate distances for all points
  const distances = new Float32Array(totalPoints);
  const batchSize = 10000; // Process in batches for better performance and progress reporting

  for (let startIdx = 0; startIdx < totalPoints; startIdx += batchSize) {
    const endIdx = Math.min(startIdx + batchSize, totalPoints);

    for (let i = startIdx; i < endIdx; i++) {
      const { worldPoint } = allPoints[i];

      // Calculate vector from cylinder center to point
      const vectorToPoint = worldPoint.clone().sub(cylinderCenter);

      // Project onto cylinder axis
      const projection = vectorToPoint.dot(axisNormalized);

      // Find closest point on axis
      const closestPointOnAxis = cylinderCenter.clone().add(
        axisNormalized.clone().multiplyScalar(projection)
      );

      // Calculate perpendicular distance
      const perpendicular = worldPoint.clone().sub(closestPointOnAxis);
      const distance = perpendicular.length();

      // Calculate deviation from expected radius
      const deviation = Math.abs(distance - cylinderRadius);

      distances[i] = deviation;
    }

    // Report progress
    if (progressCallback) {
      const progress = Math.round((endIdx / totalPoints) * 100);
      progressCallback(progress);
    }

    // Yield to event loop to keep UI responsive
    await new Promise(resolve => setTimeout(resolve, 0));
  }

  debugLog('[Corrosion] Distance calculation complete', {
    totalPoints,
    minDistance: calculateMin(distances),
    maxDistance: calculateMax(distances),
    avgDistance: calculateAverage(distances)
  });

  // Debug: Sample point distances for verification
  if (totalPoints > 0) {
    const sampleSize = Math.min(10, totalPoints);
    const samples = [];
    const maxDist = calculateMax(distances);
    for (let i = 0; i < sampleSize; i++) {
      const idx = Math.floor(i * totalPoints / sampleSize);
      samples.push({
        idx,
        worldPos: {
          x: allPoints[idx].worldPoint.x.toFixed(3),
          y: allPoints[idx].worldPoint.y.toFixed(3),
          z: allPoints[idx].worldPoint.z.toFixed(3)
        },
        distance: distances[idx].toFixed(6),
        normalized: (distances[idx] / maxDist).toFixed(3)
      });
    }
    debugLog('[Corrosion Debug] Sample point distances (verify calculations):', samples);
  }

  // Store reference to points for later use
  return { distances, allPoints };
}

/**
 * Generate comprehensive statistical analysis of corrosion severity
 *
 * Classifications (assumes point cloud in meters):
 * - Cat A (Normal): 0-2mm (0.002m) deviation (green)
 * - Cat B (Minor): 2-4mm (0.004m) deviation (yellow)
 * - Cat C (Moderate): 4-6mm (0.006m) deviation (orange)
 * - Cat D (Critical): 6mm+ (0.006m+) deviation (red)
 *
 * @param {Float32Array} distances - Array of distance deviations in meters
 * @param {number} cylinderRadius - Expected radius in meters
 * @returns {Object} Statistics object with counts, percentages, and health score
 */
export function getCorrosionStatistics(distances, cylinderRadius, thresholds = null) {
  if (!distances || distances.length === 0) {
    throw new Error('Distances array is required and must not be empty');
  }

  // Use provided thresholds or default values (in meters)
  const thresh = thresholds || {
    normalToMinor: 0.002,      // 2mm default - Cat A -> Cat B
    minorToModerate: 0.004,    // 4mm default - Cat B -> Cat C
    moderateToCritical: 0.006  // 6mm default - Cat C -> Cat D
  };

  debugLog('[Corrosion Statistics] Using thresholds:', thresh);

  const totalPoints = distances.length;

  // Count points by severity
  let normalPoints = 0;
  let minorWear = 0;
  let moderateCorrosion = 0;
  let criticalCorrosion = 0;

  let minDistance = Infinity;
  let maxDistance = -Infinity;
  let sumDistance = 0;

  // Create histogram data (20 bins)
  const histogramBins = 20;
  const histogram = new Array(histogramBins).fill(0);
  const maxHistogramDistance = thresh.moderateToCritical || 0.009; // Use critical threshold for histogram

  for (let i = 0; i < totalPoints; i++) {
    const distance = distances[i];

    sumDistance += distance;
    minDistance = Math.min(minDistance, distance);
    maxDistance = Math.max(maxDistance, distance);

    // Classify by severity using dynamic thresholds (in meters)
    if (distance < thresh.normalToMinor) {
      normalPoints++;
    } else if (distance < thresh.minorToModerate) {
      minorWear++;
    } else if (distance < thresh.moderateToCritical) {
      moderateCorrosion++;
    } else {
      criticalCorrosion++;
    }

    // Add to histogram
    const binIndex = Math.min(
      Math.floor((distance / maxHistogramDistance) * histogramBins),
      histogramBins - 1
    );
    histogram[binIndex]++;
  }

  const averageDistance = sumDistance / totalPoints;

  // Calculate percentages
  const normalPercentage = (normalPoints / totalPoints) * 100;
  const minorPercentage = (minorWear / totalPoints) * 100;
  const moderatePercentage = (moderateCorrosion / totalPoints) * 100;
  const criticalPercentage = (criticalCorrosion / totalPoints) * 100;

  // Calculate health score (0-100)
  // Formula: weighted average based on severity
  const healthScore = Math.max(0, Math.min(100,
    (normalPercentage * 1.0) +
    (minorPercentage * 0.7) +
    (moderatePercentage * 0.3) +
    (criticalPercentage * 0.0)
  ));

  const statistics = {
    totalPoints,
    normalPoints,
    minorWear,
    moderateCorrosion,
    criticalCorrosion,
    normalPercentage: parseFloat(normalPercentage.toFixed(2)),
    minorPercentage: parseFloat(minorPercentage.toFixed(2)),
    moderatePercentage: parseFloat(moderatePercentage.toFixed(2)),
    criticalPercentage: parseFloat(criticalPercentage.toFixed(2)),
    minDistance: parseFloat(minDistance.toFixed(3)),
    maxDistance: parseFloat(maxDistance.toFixed(3)),
    averageDistance: parseFloat(averageDistance.toFixed(3)),
    healthScore: parseFloat(healthScore.toFixed(1)),
    histogram,
    cylinderRadius
  };

  debugLog('[Corrosion] Statistics generated:', statistics);

  return statistics;
}

/**
 * Apply corrosion gradient to Potree material for visualization
 *
 * CRITICAL: Must set elevationRange BEFORE activeAttributeName!
 * Potree uses elevationRange to create shader uniform for normalization.
 *
 * @param {Object} material - Potree PointCloudMaterial
 * @param {Array} gradient - Gradient array [[position, THREE.Color], ...]
 * @param {number} maxDistance - Maximum distance for scaling in meters (default: 0.015m = 15mm)
 */
export function applyCorrosionGradientToMaterial(material, gradient, maxDistance = 0.015) {
  if (!material) {
    throw new Error('Material is required');
  }

  debugLog('[Corrosion] Applying gradient to material', {
    maxDistance,
    gradientStops: gradient.length,
    currentActiveAttribute: material.activeAttributeName
  });

  // CRITICAL ORDER:
  // 1. Set elevation range FIRST - this creates the shader uniform
  material.elevationRange = [0, maxDistance];

  // 2. Apply custom gradient
  material.gradient = gradient;

  // 3. Set active attribute LAST - this triggers shader recompilation
  material.activeAttributeName = 'elevation';

  debugLog('[Corrosion] Material configured:', {
    elevationRange: material.elevationRange,
    activeAttributeName: material.activeAttributeName,
    gradientApplied: true
  });

  // Diagnostic: Check if shader uniform will be created properly
  debugLog('[Diagnostic] ✅ Material elevation range set:', material.elevationRange);
}

/**
 * Store calculated distances in the elevation attribute of point cloud nodes
 *
 * This is CRITICAL for Potree's shader-based visualization.
 * Each node in the octree has its own geometry with BufferAttributes.
 *
 * @param {Object} pointCloud - Potree point cloud object
 * @param {Object} distancesData - Object with {distances, allPoints} from calculatePointDistancesToCylinder
 */
export function storeDistancesInElevationAttribute(pointCloud, distancesData) {
  const { distances, allPoints } = distancesData;

  debugLog('[Corrosion] Storing distances in elevation attribute...');

  // Group points by node
  const nodePointsMap = new Map();

  allPoints.forEach((pointData, index) => {
    const { node } = pointData;
    if (!nodePointsMap.has(node)) {
      nodePointsMap.set(node, []);
    }
    nodePointsMap.get(node).push({ index, distance: distances[index] });
  });

  // Store distances in each node's elevation attribute
  let nodesProcessed = 0;
  nodePointsMap.forEach((pointsInNode, node) => {
    if (node.geometry && node.geometry.attributes) {
      const numPointsInNode = node.geometry.attributes.position.count;

      // Create or get elevation attribute
      if (!node.geometry.attributes.elevation) {
        node.geometry.attributes.elevation = new THREE.BufferAttribute(
          new Float32Array(numPointsInNode),
          1
        );
      }

      const elevationArray = node.geometry.attributes.elevation.array;

      // Map global indices to local node indices
      // This is tricky - we need to find the local index for each point
      pointsInNode.forEach(({ distance }, localIdx) => {
        if (localIdx < numPointsInNode) {
          elevationArray[localIdx] = distance;
        }
      });

      // Mark attribute as needing update
      node.geometry.attributes.elevation.needsUpdate = true;
      nodesProcessed++;

      debugLog(`[Corrosion] ✓ Node ${nodesProcessed}: Stored ${pointsInNode.length} distances`);
    }
  });

  debugLog(`[Corrosion] Distances stored in ${nodesProcessed} nodes`);
}

/**
 * Export corrosion data to CSV format
 *
 * @param {Float32Array} distances - Array of distance deviations
 * @param {Object} statistics - Statistics object from getCorrosionStatistics
 * @param {Object} cylinderData - Cylinder parameters
 * @returns {string} CSV formatted string
 */
export function exportCorrosionDataToCSV(distances, statistics, cylinderData) {
  let csv = 'Pipeline Corrosion Analysis Report\n';
  csv += `Generated: ${new Date().toISOString()}\n\n`;

  // Cylinder Information
  csv += 'Cylinder Information\n';
  csv += `Position,${cylinderData.position.x},${cylinderData.position.y},${cylinderData.position.z}\n`;
  csv += `Axis,${cylinderData.axis.x},${cylinderData.axis.y},${cylinderData.axis.z}\n`;
  csv += `Radius,${(cylinderData.radius * 1000).toFixed(0)} mm\n`;
  csv += `Height,${(cylinderData.height * 1000).toFixed(0)} mm\n\n`;

  // Statistics Summary
  csv += 'Statistics Summary\n';
  csv += `Total Points,${statistics.totalPoints}\n`;
  csv += `Health Score,${statistics.healthScore}%\n`;
  csv += `Max Deviation,${(statistics.maxDistance * 1000).toFixed(3)} mm\n\n`;

  // Severity Breakdown
  csv += 'Severity Breakdown\n';
  csv += `Cat A - Light scale (0-2mm),${statistics.normalPoints},${statistics.normalPercentage}%\n`;
  csv += `Cat B - Moderate scale (2-4mm),${statistics.minorWear},${statistics.minorPercentage}%\n`;
  csv += `Cat C - Heavy scale (4-6mm),${statistics.moderateCorrosion},${statistics.moderatePercentage}%\n`;
  csv += `Cat D - Severe scale (6mm+),${statistics.criticalCorrosion},${statistics.criticalPercentage}%\n\n`;

  // Recommendations
  csv += 'Recommendations\n';
  if (statistics.healthScore >= 90) {
    csv += 'Pipeline is in excellent condition. Continue regular monitoring.\n';
  } else if (statistics.healthScore >= 75) {
    csv += 'Pipeline is in good condition. Schedule next inspection in 6 months.\n';
  } else if (statistics.healthScore >= 50) {
    csv += 'Pipeline shows signs of wear. Increase monitoring frequency and plan maintenance.\n';
  } else if (statistics.healthScore >= 25) {
    csv += 'Pipeline shows significant corrosion. Immediate maintenance required.\n';
  } else {
    csv += 'CRITICAL: Pipeline integrity compromised. Immediate action and possible replacement required.\n';
  }

  return csv;
}
